import type { User } from '@verantos-dev/core-annotation-tool';

export type BatchKey = 'training' | 'test' | 'validation';

/** A single configured move within a batch. `id` is client-generated. */
export interface ReallocationOperation {
  id: string;
  batch: BatchKey;
  fromAnnotatorId: string;
  toAnnotatorId: string;
  count: number;
}

export interface EditProjectState {
  editMode: boolean;
  /** Annotators chosen in the UI but not yet persisted. */
  addedAnnotators: User[];
  /** Ids of persisted annotators marked for removal. */
  removedAnnotatorIds: string[];
  addedSupervisors: User[];
  removedSupervisorIds: string[];
  /** Reallocations confirmed via the wizard; persist in UI until Save. */
  confirmedReallocations: ReallocationOperation[];
}

export const initialEditProjectState: EditProjectState = {
  editMode: false,
  addedAnnotators: [],
  removedAnnotatorIds: [],
  addedSupervisors: [],
  removedSupervisorIds: [],
  confirmedReallocations: [],
};

export type EditProjectAction =
  | { type: 'ENTER_EDIT_MODE' }
  | { type: 'EXIT_EDIT_MODE' }
  | { type: 'CANCEL_EDITS' }
  | { type: 'ADD_ANNOTATOR'; person: User }
  | { type: 'REMOVE_ANNOTATOR'; annotatorId: string }
  | { type: 'ADD_SUPERVISOR'; person: User }
  | { type: 'REMOVE_SUPERVISOR'; supervisorId: string }
  | { type: 'COMMIT_REALLOCATIONS'; operations: ReallocationOperation[] }
  | { type: 'CLEAR_REALLOCATIONS' };

/** Discards every staged edit but keeps the current edit-mode flag. */
const clearedDrafts = (editMode: boolean): EditProjectState => ({
  ...initialEditProjectState,
  editMode,
});

export const editProjectReducer = (
  state: EditProjectState,
  action: EditProjectAction
): EditProjectState => {
  switch (action.type) {
    case 'ENTER_EDIT_MODE':
      return { ...state, editMode: true };

    case 'EXIT_EDIT_MODE':
      return clearedDrafts(false);

    case 'CANCEL_EDITS':
      return clearedDrafts(false);

    case 'ADD_ANNOTATOR': {
      const { person } = action;
      // Re-adding a persisted annotator that was marked for removal: just un-mark it.
      if (state.removedAnnotatorIds.includes(person.id)) {
        return {
          ...state,
          removedAnnotatorIds: state.removedAnnotatorIds.filter((id) => id !== person.id),
        };
      }
      if (state.addedAnnotators.some((a) => a.id === person.id)) {
        return state;
      }
      return { ...state, addedAnnotators: [...state.addedAnnotators, person] };
    }

    case 'REMOVE_ANNOTATOR': {
      const { annotatorId } = action;
      // Removing a not-yet-persisted addition: drop it from the added list.
      if (state.addedAnnotators.some((a) => a.id === annotatorId)) {
        return {
          ...state,
          addedAnnotators: state.addedAnnotators.filter((a) => a.id !== annotatorId),
        };
      }
      if (state.removedAnnotatorIds.includes(annotatorId)) {
        return state;
      }
      return { ...state, removedAnnotatorIds: [...state.removedAnnotatorIds, annotatorId] };
    }

    case 'ADD_SUPERVISOR': {
      const { person } = action;
      if (state.removedSupervisorIds.includes(person.id)) {
        return {
          ...state,
          removedSupervisorIds: state.removedSupervisorIds.filter((id) => id !== person.id),
        };
      }
      if (state.addedSupervisors.some((s) => s.id === person.id)) {
        return state;
      }
      return { ...state, addedSupervisors: [...state.addedSupervisors, person] };
    }

    case 'REMOVE_SUPERVISOR': {
      const { supervisorId } = action;
      if (state.addedSupervisors.some((s) => s.id === supervisorId)) {
        return {
          ...state,
          addedSupervisors: state.addedSupervisors.filter((s) => s.id !== supervisorId),
        };
      }
      if (state.removedSupervisorIds.includes(supervisorId)) {
        return state;
      }
      return { ...state, removedSupervisorIds: [...state.removedSupervisorIds, supervisorId] };
    }

    case 'COMMIT_REALLOCATIONS':
      return { ...state, confirmedReallocations: action.operations };

    case 'CLEAR_REALLOCATIONS':
      return { ...state, confirmedReallocations: [] };

    default:
      return state;
  }
};

/** True when any people draft or reallocation is staged. */
export const computeHasChanges = (state: EditProjectState): boolean =>
  state.addedAnnotators.length > 0 ||
  state.removedAnnotatorIds.length > 0 ||
  state.addedSupervisors.length > 0 ||
  state.removedSupervisorIds.length > 0 ||
  state.confirmedReallocations.length > 0;
